import {fetchBaseQuery} from '@reduxjs/toolkit/query'
import type {BaseQueryFn, FetchArgs, FetchBaseQueryError} from '@reduxjs/toolkit/query'
import type {TRootState} from '.'
import {isTokenExpired} from '../utils/jwt-utils'

/**
 * Supports both Cognito and IOP (Single Sign) tokens — stored in root.token.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BACKEND_URL,
  prepareHeaders: (headers: Headers, {getState}: {getState: () => unknown}): Headers => {
    const token = (getState() as TRootState).root.token

    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    return headers
  },
})

/**
 * Check if a non-expired authentication token exists in the state.
 * Works for both Cognito (RS256) and IOP (HS512) JWTs since both have standard `exp` claims.
 */
function hasValidToken(state: TRootState): boolean {
  const token = state.root.token
  if (!token || token === 'null' || token === '') return false
  return !isTokenExpired(token)
}

/**
 * Wrapper around fetchBaseQuery that handles 401 responses globally.
 * Works for both Cognito and IOP (Single Sign) authentication flows:
 * - If a valid, non-expired token exists: 401 is a permission issue (don't logout)
 * - If no token or token is expired: 401 is an auth failure (clear state, redirect to /signin)
 */
export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    const state = api.getState() as TRootState
    const hasToken = hasValidToken(state)

    if (hasToken) {
      console.warn('Access denied - insufficient permissions for this resource')
    } else {
      // Expired or missing token — clear auth state so PrivateRoute redirects to /signin
      api.dispatch({type: 'rootSlice/updateCurrentUser', payload: null})
      api.dispatch({type: 'rootSlice/clearBackendRolePermissions'})
      api.dispatch({type: 'rootSlice/setAppLoaded', payload: true})

      window.localStorage.removeItem('persist:root')
      window.localStorage.removeItem('persist:combined')

      window.location.href = '/signin'
    }
  }

  return result
}
